import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useTranslation } from 'react-i18next';

import UniversalButton from "components/uiComponents/UniversalButton";
import UniversalInput from "components/uiComponents/UniversalInput";
import { FormulaTree } from "utils/services";

import { ReactComponent as CloseIcon } from "static/icons/close-or.svg";

import { useDispatch, useSelector } from "react-redux";
import {
  createColumnSchemeAC,
  setFunctionValueAC,
  setNewColumnNameValueAC,
  setNodeListAC,
  setTreeAC,
} from "store/actions/condition";
import "./ConditionModal.scss";

import { ReactComponent as DeleteIcon } from "static/icons/trash.svg";
import { useCallback } from "react";
import TabComponent from "components/uiComponents/Tabs";
import FormulaInputWithSuggestionMode from "./FormulaInputWithSuggestionMode/FormulaInputWithSuggestionMode";
import SimpleFormulaInputMode from "./SimpleFormulaInputMode/SimpleFormulaInputMode";
import { conditionTabId } from "utils/types";
import { showToast } from "utils/helpers";
import useSetting from "components/sidebarComponents/SidebarProject/use-settings";
import md5 from 'md5';

const ConditionModal = ({
  columnName,
  columnKey,
  isEdit,
  tree,
  autoActiveInputs,
  currentTabId: tabId,
  currentTableId,
  functionValue: initialFunctionValue,
  onClose,
  deleteCallback,
}) => {
  const { t } = useTranslation();
  const { transliterate } = require('transliteration');
  const { customAlphabet } = require('nanoid');
  const nanoid = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', 10);

  const [currentTabId, setCurrentTabId] = useState(conditionTabId.advance);

  const [isValidExpression, setIsValidExpression] = useState(true);

  useEffect(() => {
    const overflow = document.querySelector(".modal_wrap");

    const stopOutsideClick = (event) => {
      if (event.target.classList.contains("modal_wrap")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    overflow?.addEventListener("click", stopOutsideClick);
  }, []);

  const tabsArray = useMemo(
    () => [
      {
        id: conditionTabId.advance,
        text: t('basic'),
      },
      {
        id: conditionTabId.basic,
        text: t('custom'),
      },
    ],
    []
  );

  const {addTableColumnHandler, updateTableColumnHandler} = useSetting()

  const dispatch = useDispatch();
  const newColumnName = useSelector((state) => state.condition.newColumnName);

  const variablesForCondition = useSelector(
    (state) => state.condition.conditionVariables
  );
  const currentTree = useSelector((state) => state.condition.currentTree);
  const functionValue = useSelector((state) => state.condition.functionValue);

  const searchTreeService = useRef(
    new FormulaTree((newTree, newNodeList) => {
      dispatch(setTreeAC(newTree));
      dispatch(setNodeListAC(newNodeList));
    })
  ).current;

  useEffect(() => {
    if (columnName) setColumnNameHandler(columnName);
  }, [columnName, dispatch]);

  useEffect(() => {
    if (tabId) setCurrentTabId(tabId);
  }, [tabId, dispatch]);

  useEffect(() => {
    dispatch(setFunctionValueAC(initialFunctionValue));
  }, [initialFunctionValue, dispatch]);

  useEffect(() => {
    if (tree) {
      dispatch(setTreeAC(tree));
      searchTreeService.setNewTree(tree);
    }
  }, [tree, searchTreeService, dispatch]);

  const setColumnNameHandler = useCallback(
    (value) => {
      dispatch(setNewColumnNameValueAC(value));
    },
    [dispatch]
  );

  const onDeleteHandler = () => {
    clearPopUpHandler();
    onClose();
    deleteCallback(newColumnName);
  };

  const clearTreeHandler = useCallback(() => {
    dispatch(setTreeAC(null));
    dispatch(setNodeListAC([]));
    searchTreeService.clearTree();
    setIsValidExpression(true);
  }, [dispatch, searchTreeService]);

  const clearPopUpHandler = () => {
    clearTreeHandler();
    dispatch(setNewColumnNameValueAC(""));
    dispatch(setFunctionValueAC(""));
    onClose();
  };

  const onTabChangeHandler = (currentTabId) => {
    setCurrentTabId(currentTabId);
  };
  const containsLetters = (str) => {
    return /[a-zA-Z]/.test(str);
  }

  const onApplyHandler = () => {
    if (!containsLetters(newColumnName)) {
      showToast(false, t('name_must_contain_at_least_one_letter'));

      return;
    }
    
    const isValidExpression =
      currentTabId === conditionTabId.advance
        ? searchTreeService.isValidTree(currentTree)
        : true;

    if (!isValidExpression) {
      setIsValidExpression(false);
      return;
    }
    
    const newColumnKey = columnKey ? columnKey : transliterate(md5(newColumnName)).replace(/[^a-zA-Z]/g, '')+ nanoid();
    const functionValueString = searchTreeService.createStringExpression(currentTree);

    dispatch(
      createColumnSchemeAC({
        functionValue:
          currentTabId === conditionTabId.advance ? functionValueString : functionValue,
        variablesForCondition,
        newColumnName,
        newColumnKey,
        currentTabId,
        currentTree,
        currentTableId: currentTableId,
        prevColumnKey: columnKey,
        prevColumnName: columnName,
      })
    );
    
    if (!isEdit) {
      addTableColumnHandler(
        {
          optionName: newColumnKey,
          visibilityName: newColumnName,
          metaData: {
            isCustomColumn: true,
          },
          isCustomColumn: true
        }
      );
    } else {
      updateTableColumnHandler({
        optionName: newColumnKey,
        visibilityName: newColumnName,
        prevColumnName: columnName,
        metaData: {
          isCustomColumn: true,
        },
        isCustomColumn: true
      });
    };

    dispatch(setNewColumnNameValueAC(""));
    clearTreeHandler();

    clearPopUpHandler();
  };

  const tabsContentObject = useMemo(() => {
    return {
      [conditionTabId.advance]: (
        <div>
          <FormulaInputWithSuggestionMode
            autoActiveInputs={autoActiveInputs}
            clearTreeHandler={clearTreeHandler}
            setIsValidExpression={setIsValidExpression}
            searchTreeService={searchTreeService}
          />
          {!isValidExpression ? (
            <div className="expression-error">{t('unexpected_expression')}</div>
          ) : null}
        </div>
      ),
      [conditionTabId.basic]: <SimpleFormulaInputMode />,
    };
  }, [
    clearTreeHandler,
    searchTreeService,
    isValidExpression,
    autoActiveInputs,
  ]);

  const disabledApplyBtn =
    !newColumnName ||
    (!functionValue && currentTabId === conditionTabId.basic) ||
    (!currentTree && currentTabId === conditionTabId.advance);

  const defaultTabId =
    tree || !functionValue ? conditionTabId.advance : conditionTabId.basic;


  const title = isEdit ? t('edit_custom_function')  : t('add_custom_function')
  
  return (
    <div className="conditional-modal">
      <div className="conditional-modal__container">
        <p className="conditional-modal__title">{title}</p>

        <div className="conditional-modal__close" onClick={clearPopUpHandler}>
          <CloseIcon />
        </div>
        <div className="conditional-modal__input">
          <span className="conditional-modal__input-label">{t('column_name')}</span>
          <UniversalInput
            value={newColumnName}
            onChangeValue={setColumnNameHandler}
          />
        </div>
        <div className="conditional-modal-content-wrapper">
          <TabComponent
            tabsArray={tabsArray}
            tabsContentObject={tabsContentObject}
            defaultId={defaultTabId}
            onSetTab={onTabChangeHandler}
          />
        </div>
        <div className="hint">
          {t('you_can_use_basic_math_operations')}
          <br />
          {t('example_round')}
        </div>
      </div>
      <div className="modal_btns">
        {columnName ? (
          <div className="modal-delete-btn">
            <UniversalButton
              leftIcon={<DeleteIcon />}
              type="button"
              label={t('delete')}
              size="lg"
              labelColor="blue"
              labelHoverColor="blue"
              backgroundColor="white"
              backgroundHoverColor="blue-light"
              onClick={onDeleteHandler}
            />
          </div>
        ) : null}

        <UniversalButton
          type="button"
          label={t('close')}
          size="lg"
          labelColor="blue"
          labelHoverColor="blue"
          backgroundColor="white"
          backgroundHoverColor="blue-light"
          onClick={clearPopUpHandler}
        />

        <UniversalButton
          type="button"
          label={t('apply')}
          isDisabled={disabledApplyBtn}
          size="lg"
          onClick={onApplyHandler}
          labelColor="white"
          labelHoverColor="white"
          backgroundColor="orange"
          backgroundHoverColor="orange-light"
        />
      </div>
    </div>
  );
};

export default ConditionModal;
